// components/schema/GraphElementModal.jsx

"use client"

import React, { useEffect, useState } from "react"

const STANDARD_TYPES = [
  "STRING",
  "INT",
  "BIGINT",
  "FLOAT",
  "DOUBLE",
  "DECIMAL",
  "BOOLEAN",
  "DATE",
  "TIME",
  "DATETIME",
  "TIMESTAMP",
  "UUID",
  "JSON",
]

function createEmptyPropRow() {
  return {
    id: Math.random().toString(36).slice(2, 9),
    name: "",
    type: "",
    optional: false,
  }
}

export default function GraphElementModal({
  open,
  mode,          // "node" | "edge" | null
  edgeContext,   // { src, dst } | null
  onClose,
  onSubmit,      // (data) => void
}) {
  const [nodeType, setNodeType] = useState("")
  const [nodeLabel, setNodeLabel] = useState("")
  const [nodeProps, setNodeProps] = useState([createEmptyPropRow()])

  const [edgeType, setEdgeType] = useState("")
  const [edgeLabel, setEdgeLabel] = useState("")
  const [edgeProps, setEdgeProps] = useState([createEmptyPropRow()])

  // Reset fields when modal opens / mode changes
  useEffect(() => {
    if (!open) return

    if (mode === "node") {
      setNodeType("NodeType")
      setNodeLabel("NodeType")
      setNodeProps([createEmptyPropRow()])
    } else if (mode === "edge") {
      const src = edgeContext?.src || "Src"
      const dst = edgeContext?.dst || "Dst"
      const defaultType = `${src}_TO_${dst}`
      setEdgeType(defaultType)
      setEdgeLabel(defaultType)
      setEdgeProps([createEmptyPropRow()])
    }
  }, [open, mode, edgeContext])

  if (!open || !mode) return null

  const handleSubmit = (e) => {
    e.preventDefault()

    if (mode === "node") {
      if (!nodeType.trim()) return
      onSubmit({
        mode: "node",
        nodeType: nodeType.trim(),
        nodeLabel: (nodeLabel || "").trim(),
        props: nodeProps,
      })
    } else if (mode === "edge") {
      const src = edgeContext?.src
      const dst = edgeContext?.dst
      if (!src || !dst) return
      onSubmit({
        mode: "edge",
        edgeType: (edgeType || "").trim(),
        edgeLabel: (edgeLabel || "").trim(),
        props: edgeProps,
        src,
        dst,
      })
    }
  }

  const updateNodePropRow = (id, patch) => {
    setNodeProps((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    )
  }

  const updateEdgePropRow = (id, patch) => {
    setEdgeProps((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    )
  }

  const addNodePropRow = () => {
    setNodeProps((rows) => [...rows, createEmptyPropRow()])
  }

  const addEdgePropRow = () => {
    setEdgeProps((rows) => [...rows, createEmptyPropRow()])
  }

  const removeNodePropRow = (id) => {
    setNodeProps((rows) =>
      rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)
    )
  }

  const removeEdgePropRow = (id) => {
    setEdgeProps((rows) =>
      rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">
            {mode === "node" ? "Add Node Type" : "Add Edge Type"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          {mode === "node" && (
            <>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Node Type (identifier)
                </label>
                <input
                  value={nodeType}
                  onChange={(e) => setNodeType(e.target.value)}
                  className="w-full h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs"
                  autoFocus
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Label
                </label>
                <input
                  value={nodeLabel}
                  onChange={(e) => setNodeLabel(e.target.value)}
                  className="w-full h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs"
                  placeholder="Defaults to Node Type"
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Properties
                  </span>
                  <button
                    type="button"
                    onClick={addNodePropRow}
                    className="text-[11px] px-2 py-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    + Add property
                  </button>
                </div>

                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {nodeProps.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-2 text-xs"
                    >
                      <input
                        value={p.name}
                        onChange={(e) =>
                          updateNodePropRow(p.id, { name: e.target.value })
                        }
                        placeholder="name"
                        className="flex-1 h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"
                      />
                      <input
                        value={p.type}
                        onChange={(e) =>
                          updateNodePropRow(p.id, { type: e.target.value })
                        }
                        list="pgs-type-suggestions"
                        placeholder="STRING"
                        className="w-28 h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"
                      />
                      <label className="inline-flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={p.optional}
                          onChange={(e) =>
                            updateNodePropRow(p.id, {
                              optional: e.target.checked,
                            })
                          }
                          className="h-3 w-3"
                        />
                        <span>optional</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => removeNodePropRow(p.id)}
                        className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-700 text-[11px] hover:bg-gray-100 dark:hover:bg-gray-800"
                        title="Remove property"
                      >
                        -
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {mode === "edge" && (
            <>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="font-medium text-gray-500 dark:text-gray-400">
                    Source
                  </div>
                  <div className="mt-0.5 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 truncate">
                    {edgeContext?.src}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-500 dark:text-gray-400">
                    Target
                  </div>
                  <div className="mt-0.5 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 truncate">
                    {edgeContext?.dst}
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Edge Type (identifier)
                </label>
                <input
                  value={edgeType}
                  onChange={(e) => setEdgeType(e.target.value)}
                  className="w-full h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs"
                  autoFocus
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Label
                </label>
                <input
                  value={edgeLabel}
                  onChange={(e) => setEdgeLabel(e.target.value)}
                  className="w-full h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs"
                  placeholder="Defaults to Edge Type"
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Properties
                  </span>
                  <button
                    type="button"
                    onClick={addEdgePropRow}
                    className="text-[11px] px-2 py-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    + Add property
                  </button>
                </div>

                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {edgeProps.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-2 text-xs"
                    >
                      <input
                        value={p.name}
                        onChange={(e) =>
                          updateEdgePropRow(p.id, { name: e.target.value })
                        }
                        placeholder="since"
                        className="flex-1 h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"
                      />
                      <input
                        value={p.type}
                        onChange={(e) =>
                          updateEdgePropRow(p.id, { type: e.target.value })
                        }
                        list="pgs-type-suggestions"
                        placeholder="DATE"
                        className="w-28 h-8 px-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"
                      />
                      <label className="inline-flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={p.optional}
                          onChange={(e) =>
                            updateEdgePropRow(p.id, {
                              optional: e.target.checked,
                            })
                          }
                          className="h-3 w-3"
                        />
                        <span>optional</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => removeEdgePropRow(p.id)}
                        className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-700 text-[11px] hover:bg-gray-100 dark:hover:bg-gray-800"
                        title="Remove property"
                      >
                        -
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* datalist with standard types (for both node & edge) */}
          <datalist id="pgs-type-suggestions">
            {STANDARD_TYPES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 px-3 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-8 px-3 rounded-md bg-blue-600 text-white text-xs hover:bg-blue-700"
            >
              {mode === "node" ? "Create Node" : "Create Edge"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
